/* eslint-disable @typescript-eslint/naming-convention */
import type { FC } from "react";
import { useMemo } from "react";

import classNames from "classnames";
import styled, { css } from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Dropdown, EllipsisDropdownItem } from "@similarweb/ui-components";

import { UpgradeLink } from "app/components/React/Table/cells";
import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { trackEvent } from "app/components/React/Table/SWReactTableTrackEvent";
import { i18nFilter, translate } from "app/filters/i18nFilter";

export const CellDropdownSWReactTableOnRowHover = css`
    .flex-table {
        .swReactTable-wrapper {
            .swReactTableCell {
                &.active-row {
                    div[class^="DropdownActionItem"] {
                        background-color: ${colorsPalettes.blue[100]};
                        color: ${colorsPalettes.blue[400]};

                        svg path {
                            fill: ${colorsPalettes.blue[400]};
                        }

                        .SWReactIcons {
                            display: inline-block;
                        }
                    }
                }
            }
        }
    }
`;

const EllipsisDropdownItemStyled = styled(EllipsisDropdownItem)`
    svg {
        height: 100%;
        width: 100%;
        fill: ${colorsPalettes.carbon[200]};
    }

    div[data-automation-icon-name="open-new-window"] {
        svg {
            height: 19px;
            width: 19px;
        }
    }
`;

const DropdownActionItem = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    cursor: pointer;
    max-width: 100%;

    &:hover {
        background-color: ${colorsPalettes.blue[100]};
        color: ${colorsPalettes.blue[400]};

        svg path {
            fill: ${colorsPalettes.blue[400]} !important;
        }

        .SWReactIcons {
            display: inline-block;
        }
    }

    .SWReactIcons {
        display: none;
        height: 16px;
        width: 10px;
        flex: 0 0 auto;
        svg {
            height: 100% !important;
            width: 100% !important;
        }
    }
`;

const ShowMoreButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    color: ${colorsPalettes.blue[400]};
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    text-align: left;
`;

const ExpandIcon = styled.i`
    cursor: pointer;
`;

interface IDropDownItem {
    text: string;
    icon?: IconName;
    onClick: (value: string) => void;
}

type DropdownItems = Array<IDropDownItem>;

const defaultDropdownItems: DropdownItems = [
    {
        get text() {
            return translate("search.pages.table.columns.copy");
        },
        icon: "organic-link",
        onClick: (value: string) => {
            navigator.clipboard.writeText(value);
        },
    },
    {
        get text() {
            return translate("search.pages.table.columns.open");
        },
        icon: "open-new-window",
        onClick: (value: string) => {
            window.open(`http://${value}`, "_blank", "noopener,noreferrer");
        },
    },
];

const CellSiteWrapper = styled.div<{ isChild: boolean; showMore: boolean }>`
    display: flex;
    gap: ${({ isChild }) => (isChild ? "0" : "4px")};
    max-width: 98%;
    flex-direction: ${({ isChild }) => (isChild ? "column" : "row")};
    align-items: ${({ isChild }) => (isChild ? "flex-start" : "center")};
    margin-top: ${({ showMore }) => (showMore ? "-5px" : "0")};
`;

const DropdownActionItemContent = styled.div`
    flex: 1 1 auto;
    text-overflow: ellipsis;
    overflow: hidden;
`;

export const CellSiteWithDropdown: FC<Partial<ITableCellProps> & { dropDownOptions: DropdownItems }> = ({
    row,
    value,
    toggleChildRows,
    showMoreChildRows,
    visibleParents,
    tableOptions,
    dropDownOptions = [],
}) => {
    const children = useMemo(() => row.Children || [], [row.Children]);
    const isChild = row.parent;
    const isExpanded = visibleParents?.[row.index] && visibleParents[row.index].expanded;
    const showMore = isChild && row.isLast && row.index + 1 !== row.parent.childsCount;

    const handleDropdownOnClick = (data: { children: string }) => {
        trackEvent(tableOptions, "Internal Link", value, "click");

        [...defaultDropdownItems, ...dropDownOptions].find((item) => item.text === data.children)?.onClick(value);
    };

    const dropdownActionItem = useMemo(
        () => (
            <DropdownActionItem title={value}>
                <DropdownActionItemContent>
                    {`${value} ${children.length ? `(${children.length})` : ""}`}
                </DropdownActionItemContent>
                <SWReactIcons iconName="arrow" />
            </DropdownActionItem>
        ),
        [value, children],
    );

    const dropDownActions = useMemo(
        () =>
            [...dropDownOptions, ...defaultDropdownItems].map((option) => (
                <EllipsisDropdownItemStyled
                    key={option.text}
                    iconName={option.icon}
                >
                    {option.text}
                </EllipsisDropdownItemStyled>
            )),
        [dropDownOptions],
    );

    if (value === "grid.upgrade") {
        return <UpgradeLink />;
    }

    return (
        <CellSiteWrapper
            isChild={isChild}
            showMore={showMore}
        >
            {children.length ? (
                <ExpandIcon
                    className={classNames({
                        "sw-icon-collapse": isExpanded,
                        "sw-icon-expand": !isExpanded,
                    })}
                    onClick={() => toggleChildRows?.(row, value)}
                />
            ) : null}
            {
                <Dropdown
                    key={value}
                    width={220}
                    appendTo="body"
                    dropdownPopupPlacement="bottom-left"
                    onClick={handleDropdownOnClick}
                >
                    {[dropdownActionItem, ...dropDownActions]}
                </Dropdown>
            }

            {showMore ? (
                <ShowMoreButton onClick={() => showMoreChildRows?.(row, value)}>
                    {i18nFilter()("grid.showmore")}
                </ShowMoreButton>
            ) : null}
        </CellSiteWrapper>
    );
};
